import { useFlag } from '@calendly/feature-check-react';
import { HtmlParser } from '@calendly/html-parser';
import { I18n } from '@calendly/i18n';
import { Button } from '@calendly/ui/components/button';
import { Modal } from '@calendly/ui/components/modal';
import { BodyText, HeadingText } from '@calendly/ui/components/typography';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import classNames from 'classnames';
import { useEffect, useState, forwardRef } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import { useGetUserQuery, useUpdateUserActivationDataMutation } from 'common';
import { trialTypeSelector, trialEndsInDaysSelector, isTrialActiveSelector } from 'features/billing/selectors';
import { MI_MEETINGS_TOUR } from 'features/meeting_intelligence';
import { TRACKING_EVENT_NAMES } from 'hooks/analytics_events/constants';
import { useTrack } from 'hooks/analytics_events/useTrack';
import AiModalImg from './images/ai-modal.svg';
import { Wrapper, ModalContent, HeadingContainer, SidebarWithImage, ContentContainer, BulletPoint, CloseButton } from './shared';
const noticeModalContentIsTabletUpCss = "n1mhs6qk";
const noticeModalContentCss = "n175ejmg";
const NoticeModalContent = forwardRef((props, ref) => {
  const isTabletUp = useDeviceType('isTabletUp');
  return <ModalContent ref={ref} {...props} className={classNames(noticeModalContentCss, {
    [noticeModalContentIsTabletUpCss]: isTabletUp
  }, props.className)} />;
});
const centeredContainerCss = "c4dwcu5";
const CenteredContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(centeredContainerCss, props.className)} />;
});
const footerContainerCss = "fm7m1sy";
const FooterContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(footerContainerCss, props.className)} />;
});
const fullWidthButtonCss = "fi5lcmz";
const FullWidthButton = forwardRef((props, ref) => <Button ref={ref} {...props} className={classNames(fullWidthButtonCss, props.className)} />);
export const AiTrialNoticeModal = () => {
  const [isVisible, setIsVisible] = useState(false);
  const history = useHistory();
  const {
    pathname
  } = useLocation();
  const trialActive = useSelector(isTrialActiveSelector);
  const trialEndsInDays = useSelector(trialEndsInDaysSelector);
  const trialType = useSelector(trialTypeSelector);
  const isMIEnabled = useFlag('meeting_intelligence');
  const track = useTrack();
  const {
    data: {
      organization_membership: {
        is_admin_or_owner: isAdminOrOwner
      } = {}
    } = {}
  } = useGetUserQuery();
  const [update] = useUpdateUserActivationDataMutation();
  const {
    data: {
      id: userId,
      activation_data: {
        has_seen_ai_trial_notice_modal: hasSeenAiTrialNoticeModal
      }
    }
  } = useGetUserQuery();
  const isIneligible = !trialActive || trialType !== 'calendly_all' || trialEndsInDays <= 0 || isAdminOrOwner || hasSeenAiTrialNoticeModal || !isMIEnabled;
  const closeModal = () => {
    update({
      has_seen_ai_trial_notice_modal: true
    });
    track({
      name: TRACKING_EVENT_NAMES.MODAL_INTERACTION,
      properties: {
        path: pathname,
        action: 'dismissed',
        modal_name: 'ai_notetaker_org_joiner_trial'
      }
    });
    setIsVisible(false);
  };
  const onLearnMore = () => {
    update({
      has_seen_ai_trial_notice_modal: true
    });
    track({
      name: TRACKING_EVENT_NAMES.FLOW_STARTED,
      properties: {
        flow_location: 'org_joined_acknowledge_trial_modal',
        flow_name: 'ai_notetaker_contextual_help',
        path: pathname
      }
    });
    window.open('https://help.calendly.com/hc/en-us/articles/21652725311383-AI-Notetaker-overview', '_blank', 'noopener,noreferrer');
  };
  const onViewMeetings = () => {
    update({
      has_seen_ai_trial_notice_modal: true
    });
    track({
      name: TRACKING_EVENT_NAMES.MODAL_INTERACTION,
      properties: {
        path: pathname,
        action: 'acknowledged',
        modal_name: 'ai_notetaker_org_joiner_trial'
      }
    });
    setIsVisible(false);
    history.push(`/app/meetings/user/me?${MI_MEETINGS_TOUR.STEP_ONE}=true`);
  };
  useEffect(() => {
    if (!isVisible && !!userId && !isIneligible) {
      setIsVisible(true);
    }
  }, [isVisible, userId, isIneligible, setIsVisible]);
  if (isIneligible) return null;
  return <Modal id="ai-trial-notice-modal" isOpen={isVisible} onClose={closeModal}>
      <Wrapper>
        <NoticeModalContent>
          <CloseButton onClick={closeModal} />
          <SidebarWithImage src={AiModalImg} alt="ai-img" />
          <ContentContainer>
            <div>
              <HeadingContainer>
                <HeadingText level={1}>
                  {I18n.t('publisher.meeting_intelligence.trial.notice.header', {
                  count: trialEndsInDays
                })}
                </HeadingText>
              </HeadingContainer>
              <BodyText>
                {I18n.t('publisher.meeting_intelligence.trial.notice.subheader')}
              </BodyText>
              <div>
                <BulletPoint>
                  {HtmlParser(I18n.t('publisher.meeting_intelligence.trial.notice.bullet_one'))}
                </BulletPoint>
                <BulletPoint>
                  {HtmlParser(I18n.t('publisher.meeting_intelligence.trial.notice.bullet_two'))}
                </BulletPoint>
                <BulletPoint>
                  {HtmlParser(I18n.t('publisher.meeting_intelligence.trial.notice.bullet_three'))}
                </BulletPoint>
              </div>
              <CenteredContainer>
                <BodyText size="medium" color="muted">
                  {I18n.t('publisher.meeting_intelligence.trial.warning')}
                </BodyText>
              </CenteredContainer>
            </div>
            <FooterContainer>
              <FullWidthButton decoration="secondary-outline" onClick={onLearnMore}>
                {I18n.t('publisher.meeting_intelligence.trial.learn_more')}
              </FullWidthButton>
              <FullWidthButton decoration="meeting-intelligence" onClick={onViewMeetings}>
                {I18n.t('publisher.meeting_intelligence.trial.view_meetings')}
              </FullWidthButton>
            </FooterContainer>
          </ContentContainer>
        </NoticeModalContent>
      </Wrapper>
    </Modal>;
};

require("./notice_modal.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./notice_modal.js");